import React, { useMemo } from 'react';
import clsx from 'clsx';
import RateBars from '@/components/RateBars/RateBars';
import { BadgeRating } from '@/components/Badge/Badge';
import { APP_COLORS } from '@/config/colors';

import warningSymbol from '@/assets/warning-symbol.png';
import okSymbol from '@/assets/ok-symbol.png';
import noSymbol from '@/assets/no-symbol.png';
import { SVGIcon } from '@/components/SVGIcon/SVGIcon';
import { getVerdict, Verdict } from '@/helpers/ratingState';

// Funkcja do mapowania komunikatów na podstawie oceny

export type CardSectionRateVariant = 'default' | 'light';

export interface CardSectionRateProps {
  /**
   * The title of the section being rated
   */
  title: string;
  /**
   * Rating value from 1 to 5
   */
  rating: number;
  /**
   * Component variant - default shows full rating details, light shows simplified version
   */
  ratingMessage?: string;
  variant?: CardSectionRateVariant;
  /**
   * Description text shown in the footer (for default variant)
   */
  description?: string;
  /**
   * Verification date shown in light variant
   */
  verificationDate?: string;
  /**
   * Maximum rating value
   */
  maxRating?: number;
  /**
   * Optional className for custom styling
   */
  className?: string;
}

export const CardSectionRate: React.FC<CardSectionRateProps> = ({
  title,
  rating = 1,
  variant = 'default',
  description,
  verificationDate,
  ratingMessage,
  maxRating = 5,
  className,
}) => {
  const img = useMemo(() => {
    const verdict = getVerdict(rating);
    if (verdict === Verdict.NEGATIVE) return noSymbol;
    if (verdict === Verdict.NEUTRAL) return warningSymbol;
    if (verdict === Verdict.POSITIVE) return okSymbol;
    return okSymbol;
  }, [rating]);

  const formatDate = useMemo(() => {
    if (!verificationDate) return 'Zweryfikowano: --.--.----';
    return `Zweryfikowano: ${verificationDate}`;
  }, [verificationDate]);

  const isLight = variant === 'light';

  return (
    <div
      className={clsx(
        'overflow-hidden relative flex-1 w-full bg-white shadow-2xl md:max-w-[403px]',
        isLight
          ? 'rounded-3xl border border-primary-100 md:min-w-[400px]'
          : 'rounded-3xl border border-violet-100 border-solid',
        className,
      )}
    >
      {isLight ? (
        /* Light variant layout */
        <div className="flex relative flex-col gap-6 px-6 pt-6 pb-5">
          {/* Header z tytułem i badge */}
          <div className="flex flex-col">
            <h3 className="text-xl font-bold text-text-primary">{title}</h3>
            <BadgeRating
              rating={rating}
              version="v2"
              badgeText={ratingMessage}
            />
          </div>

          {/* Blok oceny - data weryfikacji */}
          <div className="flex flex-col gap-0.5">
            <div className="text-sm leading-[1em] text-grayText">
              {formatDate}
            </div>
          </div>

          <div className="absolute -bottom-4 -right-4 max-w-[150px]">
            <img src={img} alt="rating" />
          </div>
        </div>
      ) : (
        /* Default variant layout */
        <div className="pt-6 pl-6 w-full">
          <div className="flex flex-col w-full font-bold">
            <div className="text-xl leading-snug text-zinc-800">{title}</div>
            <BadgeRating rating={rating} version="v2" />
          </div>
          <div className="flex relative items-end pb-5 mt-6 w-full">
            <RateBars maxRating={5} rating={rating} />
            <div className="flex flex-col">
              <div className="flex items-end">
                <span className="text-[40px] font-extrabold leading-[40px] text-text-primary">
                  {rating}
                </span>
                <span
                  className="pl-2 text-[24px] font-normal leading-[30px]"
                  style={{ color: APP_COLORS.gray[400] }}
                >
                  /{maxRating}
                </span>
              </div>
              <div className="max-w-[150px] text-base font-bold text-text-primary">
                {ratingMessage}
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 max-w-[150px]">
              <img src={img} alt="rating" />
            </div>
          </div>
        </div>
      )}
      {/* Footer - tylko dla default variant */}
      {!isLight && description && (
        <div className="relative z-[2] flex w-full border-t border-violet-100 bg-white px-6 py-5 text-sm leading-6 text-grayText">
          <svg viewBox="0 0 30 28" fill="none" xmlns="http://www.w3.org/2000/svg" className='min-w-[30px] max-w-[40px] flex-1'>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12.2336 4.61207C13.4426 2.46264 16.5356 2.46264 17.743 4.61207L26.562 20.2902C27.7474 22.3986 26.2254 25 23.8089 25H6.16934C3.75124 25 2.22925 22.3986 3.4146 20.2902L12.2336 4.61207ZM16.5688 20.2602C16.5688 20.6794 16.4023 21.0814 16.1059 21.3778C15.8095 21.6741 15.4075 21.8407 14.9883 21.8407C14.5691 21.8407 14.1671 21.6741 13.8708 21.3778C13.5744 21.0814 13.4078 20.6794 13.4078 20.2602C13.4078 19.841 13.5744 19.439 13.8708 19.1426C14.1671 18.8463 14.5691 18.6797 14.9883 18.6797C15.4075 18.6797 15.8095 18.8463 16.1059 19.1426C16.4023 19.439 16.5688 19.841 16.5688 20.2602ZM14.9883 7.61652C14.5691 7.61652 14.1671 7.78304 13.8708 8.07943C13.5744 8.37582 13.4078 8.77782 13.4078 9.19698V13.9384C13.4078 14.3575 13.5744 14.7595 13.8708 15.0559C14.1671 15.3523 14.5691 15.5188 14.9883 15.5188C15.4075 15.5188 15.8095 15.3523 16.1059 15.0559C16.4023 14.7595 16.5688 14.3575 16.5688 13.9384V9.19698C16.5688 8.77782 16.4023 8.37582 16.1059 8.07943C15.8095 7.78304 15.4075 7.61652 14.9883 7.61652Z" fill="#FFD91B" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M3.41608 20.2902L12.2465 4.61207C13.4571 2.46264 16.5541 2.46264 17.7632 4.61207L26.5936 20.2902C27.7805 22.3986 26.2565 25 23.8369 25H6.1744C3.75315 25 2.22919 22.3986 3.41608 20.2902ZM29.2078 18.8186C31.5231 22.9312 28.5463 28 23.8369 28H6.1744C1.4647 28 -1.51401 22.9324 0.801853 18.8186L9.6326 3.13983C11.9895 -1.04479 18.0198 -1.04843 20.3771 3.13983L20.3779 3.14129L29.2078 18.8186Z" fill="#FFEE96" />
          </svg>
          <div className="pl-5">{description}</div>
        </div>
      )}
    </div>
  );
};
